import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import {
  ShieldCheck, Users, Film, Radio, Clipboard, CheckCircle2, XCircle, Star,
  BarChart3, Layers, Megaphone, Wallet, DollarSign, Flag, TrendingUp,
  Bell, FileText, Cog, Plus, Trash2, Eye, Ban, UserCheck, StopCircle, Search,
  Shield, Receipt, Award, Beaker, Building2, Zap, Activity, Image as ImageIcon, Baby, Trophy, Plug, Gift, Handshake, Tv as TvIcon, Mail, Newspaper, BookOpen, Banknote, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import CrewAdminTab from "./admin/CrewAdminTab";
import CommercialsAdminTab from "./admin/CommercialsAdminTab";
import AdManagerTab from "./admin/AdManagerTab";
import KidsAdminTab from "./admin/KidsAdminTab";
import KidCreatorsAdminTab from "./admin/KidCreatorsAdminTab";
import RadioAdminTab from "./admin/RadioAdminTab";
import RadioHostsAdminTab from "./admin/RadioHostsAdminTab";
import IntegrationsAdminTab from "./admin/IntegrationsAdminTab";
import ReferralsAdminTab from "./admin/ReferralsAdminTab";
import TalentContestAdminTab from "./admin/TalentContestAdminTab";
import SponsorshipAdminTab from "./admin/SponsorshipAdminTab";
import TvAppAdminTab from "./admin/TvAppAdminTab";
import TvGuideAdminTab from "./admin/TvGuideAdminTab";
import KidBotsAdminTab from "./admin/KidBotsAdminTab";
import AllKidProfilesAdminTab from "./admin/AllKidProfilesAdminTab";
import BotCommentsAdminTab from "./admin/BotCommentsAdminTab";
import WalletAdminTab from "./admin/WalletAdminTab";
import PendingVerificationsTab from "./admin/PendingVerificationsTab";
import NewsAdminTab from "./admin/NewsAdminTab";
import TrainingMapTab from "./admin/TrainingMapTab";
import BankManagerTab from "./admin/BankManagerTab";
import StreamReviewTab from "./admin/StreamReviewTab";
import GreenScreenAdminTab from "./admin/GreenScreenAdminTab";
import StaffSmsAdminTab from "./admin/StaffSmsAdminTab";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "hero", label: "Hero Slides", icon: ImageIcon },
  { id: "kids", label: "Kids Queue", icon: Baby },
  { id: "kids_manage", label: "Kids Channel", icon: Baby },
  { id: "kid_creators", label: "Kid Creators", icon: Baby },
  { id: "kid_bots", label: "Kid Bots", icon: Baby },
  { id: "bot_comments", label: "Bot Comments", icon: Baby },
  { id: "all_kid_profiles", label: "All Kid Profiles", icon: Baby },
  { id: "awards", label: "Awards", icon: Trophy },
  { id: "payments", label: "Payments", icon: DollarSign },
  { id: "subscriptions", label: "Subscriptions", icon: DollarSign },
  { id: "promos", label: "Promos", icon: Megaphone },
  { id: "ppv", label: "PPV Splits", icon: Receipt },
  { id: "dropship", label: "Dropship", icon: ImageIcon },
  { id: "orders", label: "Orders", icon: Receipt },
  { id: "coupons", label: "Coupons", icon: Receipt },
  { id: "fulfillment", label: "Fulfillment", icon: Receipt },
  { id: "realtime", label: "Live Metrics", icon: Activity },
  { id: "applications", label: "Applications", icon: Clipboard },
  { id: "users", label: "Users", icon: Users },
  { id: "content", label: "Content", icon: Film },
  { id: "categories", label: "Categories", icon: Layers },
  { id: "partners", label: "Partners", icon: Building2 },
  { id: "dmca", label: "DMCA / Strikes", icon: Shield },
  { id: "taxes", label: "Tax Forms", icon: Receipt },
  { id: "verify", label: "Verification", icon: Award },
  { id: "pending_verifications", label: "Pending Email Confirms", icon: Mail },
  { id: "experiments", label: "A/B Experiments", icon: Beaker },
  { id: "trending", label: "Trending Tuner", icon: Zap },
  { id: "commercials", label: "Commercials + Guide", icon: Film },
  { id: "news", label: "Tap In News", icon: Newspaper },
  { id: "bank", label: "Bank Manager", icon: Banknote },
  { id: "training", label: "Training Map", icon: BookOpen },
  { id: "radio", label: "Radio Network", icon: Radio },
  { id: "radio_hosts", label: "Radio Hosts", icon: Radio },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "staff_sms", label: "Staff SMS", icon: MessageSquare },
  { id: "referrals", label: "Referrals", icon: Gift },
  { id: "contest", label: "Talent Search", icon: Trophy },
  { id: "sponsorship", label: "Sponsorship", icon: Handshake },
  { id: "tv_app", label: "TV App", icon: TvIcon },
  { id: "tv_guide", label: "TV Guide", icon: TvIcon },
  { id: "ads", label: "Ads", icon: Megaphone },
  { id: "ad_networks", label: "Ad Networks", icon: DollarSign },
  { id: "payouts", label: "Payouts", icon: Wallet },
  { id: "wallets", label: "Wallets / Promos", icon: Wallet },
  { id: "revenue", label: "Revenue", icon: DollarSign },
  { id: "live", label: "Live Ops", icon: Radio },
  { id: "stream_review", label: "Stream Review", icon: Radio },
  { id: "greenscreen", label: "Green-Screen Library", icon: ImageIcon },
  { id: "reports", label: "Reports", icon: Flag },
  { id: "analytics", label: "Analytics", icon: TrendingUp },
  { id: "announcements", label: "Announcements", icon: Bell },
  { id: "audit", label: "Audit Log", icon: FileText },
  { id: "crew", label: "Tap In Crew", icon: Users },
  { id: "settings", label: "Settings", icon: Cog },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");

  // Lock the page scroll while the admin console is mounted so only the
  // sidebar + main pane scroll. Restore on unmount so other pages stay
  // scrollable.
  useEffect(() => {
    const prev = { html: document.documentElement.style.overflow, body: document.body.style.overflow };
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prev.html;
      document.body.style.overflow = prev.body;
    };
  }, []);

  return (
    <div data-testid="admin-page" className="fixed inset-0 pt-20 bg-[#09081a] text-white">
      {/* Inline styles to make the admin sidebar's internal scrollbar a
          subtle 6px instead of the chunky default. */}
      <style>{`
        .admin-nav-scroll::-webkit-scrollbar { width: 6px; }
        .admin-nav-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 6px; }
        .admin-nav-scroll::-webkit-scrollbar-track { background: transparent; }
        .admin-nav-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.12) transparent; }
      `}</style>
      <div className="h-full flex flex-col">
        <div className="px-3 sm:px-6 md:px-10 pb-4 pt-4 border-b border-white/10 shrink-0">
          <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Network Operations
          </div>
          <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-white tracking-tighter mt-1">Admin Console</h1>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr] min-h-0">
          {/* Sidebar — internal scroll, fixed to viewport */}
          <aside className="border-r border-white/10 p-2 lg:p-3 overflow-y-auto admin-nav-scroll lg:h-full lg:max-h-full bg-black/30" data-testid="admin-sidebar">
            <nav className="flex lg:flex-col gap-1 min-w-max lg:min-w-0" data-testid="admin-nav">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`admin-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                      active ? "bg-tv-accent text-black shadow-lg" : "text-white/75 hover:bg-white/5"
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {t.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main content pane — own internal scrollbar */}
          <main className="p-4 sm:p-6 lg:p-8 overflow-y-auto admin-nav-scroll" data-testid="admin-main">
          {tab === "overview" && <Overview />}
          {tab === "hero" && <HeroSlidesTab />}
          {tab === "kids" && <KidsQueueTab />}
          {tab === "kids_manage" && <KidsAdminTab />}
          {tab === "kid_creators" && <KidCreatorsAdminTab />}
          {tab === "kid_bots" && <KidBotsAdminTab />}
          {tab === "bot_comments" && <BotCommentsAdminTab />}
          {tab === "all_kid_profiles" && <AllKidProfilesAdminTab />}
          {tab === "awards" && <AwardsAdminTab />}
          {tab === "payments" && <PaymentsAdminTab />}
          {tab === "subscriptions" && <SubscriptionsAdminTab />}
          {tab === "promos" && <PromosAdminTab />}
          {tab === "ppv" && <PpvSplitsTab />}
          {tab === "dropship" && <DropshipAdminTab />}
          {tab === "orders" && <OrdersAdminTab />}
          {tab === "coupons" && <CouponsAdminTab />}
          {tab === "fulfillment" && <FulfillmentAdminTab />}
          {tab === "realtime" && <RealtimeMetrics />}
          {tab === "applications" && <Applications />}
          {tab === "users" && <UsersTab />}
          {tab === "content" && <Content />}
          {tab === "categories" && <Categories />}
          {tab === "partners" && <Partners />}
          {tab === "dmca" && <DMCA />}
          {tab === "taxes" && <TaxForms />}
          {tab === "verify" && <Verification />}
          {tab === "pending_verifications" && <PendingVerificationsTab />}
          {tab === "experiments" && <Experiments />}
          {tab === "trending" && <TrendingTuner />}
          {tab === "ads" && <Ads />}
          {tab === "ad_networks" && <AdManagerTab />}
          {tab === "payouts" && <Payouts />}
          {tab === "wallets" && <WalletAdminTab />}
          {tab === "revenue" && <Revenue />}
          {tab === "live" && <LiveOps />}
          {tab === "reports" && <Reports />}
          {tab === "analytics" && <Analytics />}
          {tab === "announcements" && <Announcements />}
          {tab === "audit" && <AuditLog />}
          {tab === "crew" && <CrewAdminTab />}
          {tab === "commercials" && <CommercialsAdminTab />}
          {tab === "news" && <NewsAdminTab />}
          {tab === "bank" && <BankManagerTab />}
          {tab === "stream_review" && <StreamReviewTab />}
          {tab === "training" && <TrainingMapTab />}
          {tab === "radio" && <RadioAdminTab />}
          {tab === "radio_hosts" && <RadioHostsAdminTab />}
          {tab === "integrations" && <IntegrationsAdminTab />}
          {tab === "staff_sms" && <StaffSmsAdminTab />}
          {tab === "referrals" && <ReferralsAdminTab />}
          {tab === "contest" && <TalentContestAdminTab />}
          {tab === "sponsorship" && <SponsorshipAdminTab />}
          {tab === "tv_app" && <TvAppAdminTab />}
          {tab === "tv_guide" && <TvGuideAdminTab />}
          {tab === "greenscreen" && <GreenScreenAdminTab />}
          {tab === "settings" && <Settings />}
        </main>
      </div>
      </div>
    </div>
  );
}

// ============================ SHARED UI ============================
function Stat({ label, value, accent, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className={`tv-glass rounded-xl p-4 ${accent ? "border-tv-accent/40" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${accent ? "text-tv-accent" : "text-white/30"}`} />}
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-tv-accent" : "text-white"}`}>{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="font-display font-bold text-2xl text-white mb-4">{children}</h2>;
}

function Empty({ children }) {
  return <div className="text-white/50 text-sm py-6">{children}</div>;
}

function Pill({ status }) {
  const map = {
    pending: "bg-yellow-500/20 text-yellow-300",
    open: "bg-yellow-500/20 text-yellow-300",
    approved: "bg-green-500/20 text-green-400",
    resolved: "bg-green-500/20 text-green-400",
    paid: "bg-green-500/20 text-green-400",
    active: "bg-green-500/20 text-green-400",
    denied: "bg-red-500/20 text-red-400",
    rejected: "bg-red-500/20 text-red-400",
    paused: "bg-white/10 text-white/60",
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[status] || "bg-white/10 text-white/60"}`}>{status}</span>;
}

// ============================ OVERVIEW ============================
function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  const k = data.kpis;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total users" value={k.total_users.toLocaleString()} icon={Users} testId="kpi-users" />
        <Stat label="Videos" value={k.total_videos.toLocaleString()} icon={Film} />
        <Stat label="Live now" value={k.live_now} icon={Radio} accent />
        <Stat label="Total views" value={k.total_views.toLocaleString()} icon={Eye} />
        <Stat label="Revenue (USD)" value={`$${k.total_revenue_usd.toLocaleString()}`} icon={DollarSign} accent />
        <Stat label="Pending apps" value={k.pending_applications} icon={Clipboard} />
        <Stat label="Pending payouts" value={k.pending_payouts} icon={Wallet} />
        <Stat label="Open reports" value={k.open_reports} icon={Flag} accent />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <SectionTitle>Subscriptions by tier</SectionTitle>
          <div className="tv-glass rounded-xl p-5 space-y-3">
            {Object.entries(data.subs_by_tier).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between" data-testid={`subs-${tier}`}>
                <div className="text-white/80 uppercase text-xs tracking-wider">{tier.replace("_", " ")}</div>
                <div className="font-display font-bold text-white text-lg">{count}</div>
              </div>
            ))}
          </div>

          <SectionTitle><span className="mt-6 block">Top creators</span></SectionTitle>
          <div className="space-y-2">
            {data.top_creators.map((c) => (
              <div key={c.user_id} className="tv-glass rounded-lg p-3 flex items-center gap-3">
                <img src={c.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div className="flex-1">
                  <div className="text-white font-semibold">{c.name}</div>
                  <div className="text-xs text-white/50">{c.follower_count?.toLocaleString()} followers</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-tv-brand">{c.role.replace("_", " ")}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <SectionTitle>Recent videos</SectionTitle>
          <div className="space-y-2">
            {data.recent_videos.map((v) => (
              <div key={v.id} className="tv-glass rounded-lg p-3 flex items-center gap-3">
                <img src={v.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">{v.title}</div>
                  <div className="text-xs text-white/50">{v.creator_name} · {v.category}</div>
                </div>
                <div className="text-xs text-white/70">{v.view_count?.toLocaleString()} views</div>
              </div>
            ))}
          </div>

          <SectionTitle><span className="mt-6 block">Recent users</span></SectionTitle>
          <div className="space-y-2">
            {data.recent_users.map((u) => (
              <div key={u.user_id} className="tv-glass rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-tv-brand text-black font-bold flex items-center justify-center">{u.name?.[0]}</div>
                <div className="flex-1">
                  <div className="text-white font-semibold">{u.name}</div>
                  <div className="text-xs text-white/50">{u.email}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-tv-brand">{u.role}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================ APPLICATIONS ============================
function Applications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => {
    const url = filter === "all" ? "/admin/applications" : `/admin/applications?status=${filter}`;
    api.get(url).then((r) => setApps(r.data));
  };
  useEffect(() => { load(); }, [filter]);
  const decide = async (app, decision, tier) => {
    await api.post(`/admin/applications/${app.id}/decide`, { decision, tier });
    toast.success(`${decision}`);
    load();
  };
  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {["pending", "approved", "denied", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`app-filter-${s}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ${filter === s ? "bg-white/20 text-white" : "text-white/60 hover:bg-white/5"}`}>{s}</button>
        ))}
      </div>
      {apps.length === 0 ? <Empty>No applications in this bucket.</Empty> : (
        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.id} data-testid={`app-${a.id}`} className="tv-glass rounded-xl p-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="font-display font-bold text-white text-lg">{a.display_name}</div>
                  <div className="text-xs text-white/50 uppercase tracking-wider">{a.user_email} · {a.category}</div>
                </div>
                <Pill status={a.status} />
              </div>
              <p className="text-white/80 text-sm mt-3">{a.bio}</p>
              {a.status === "pending" && (
                <div className="flex gap-2 mt-4 flex-wrap">
                  <button onClick={() => decide(a, "approve", "creator")} data-testid={`approve-creator-${a.id}`} className="bg-tv-brand text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Creator</button>
                  <button onClick={() => decide(a, "approve", "premium_creator")} data-testid={`approve-premium-${a.id}`} className="bg-tv-accent text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1"><Star className="w-4 h-4" /> Premium</button>
                  <button onClick={() => decide(a, "deny")} className="bg-red-500/80 text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1"><XCircle className="w-4 h-4" /> Deny</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ USERS ============================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (u, role) => { await api.post(`/admin/users/${u.user_id}/role?role=${role}`); toast.success(`Role: ${role}`); load(); };
  const suspend = async (u) => {
    const reason = window.prompt("Suspension reason?", "Policy violation");
    if (reason == null) return;
    await api.post(`/admin/users/${u.user_id}/suspend?reason=${encodeURIComponent(reason)}`);
    toast.success("Suspended");
    load();
  };
  const unsuspend = async (u) => { await api.post(`/admin/users/${u.user_id}/unsuspend`); toast.success("Unsuspended"); load(); };
  const filtered = users.filter((u) => !q || u.name?.toLowerCase().includes(q.toLowerCase()) || u.email?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/60" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="bg-transparent outline-none text-sm text-white flex-1" data-testid="user-search" />
        </div>
        <div className="text-sm text-white/60">{filtered.length} / {users.length}</div>
      </div>
      <div className="overflow-auto tv-glass rounded-xl">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-white/50">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Subscription</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-white/5">
                <td className="px-4 py-3 text-white">{u.name}</td>
                <td className="px-4 py-3 text-white/70">{u.email}</td>
                <td className="px-4 py-3 text-tv-brand uppercase text-xs">{u.role}</td>
                <td className="px-4 py-3 text-white/60 uppercase text-xs">{u.subscription_tier}</td>
                <td className="px-4 py-3">{u.is_suspended ? <Pill status="denied" /> : <Pill status="active" />}</td>
                <td className="px-4 py-3 flex items-center gap-2">
                  <select defaultValue={u.role} onChange={(e) => setRole(u, e.target.value)} className="bg-[#0B1120] border border-white/10 rounded px-2 py-1 text-white text-xs">
                    <option value="viewer">viewer</option>
                    <option value="creator">creator</option>
                    <option value="premium_creator">premium_creator</option>
                    <option value="admin">admin</option>
                  </select>
                  {u.is_suspended ? (
                    <button onClick={() => unsuspend(u)} data-testid={`unsuspend-${u.user_id}`} className="text-xs bg-green-500/80 text-white font-bold px-2 py-1 rounded flex items-center gap-1"><UserCheck className="w-3 h-3" /> Restore</button>
                  ) : (
                    <button onClick={() => suspend(u)} data-testid={`suspend-${u.user_id}`} className="text-xs bg-red-500/80 text-white font-bold px-2 py-1 rounded flex items-center gap-1"><Ban className="w-3 h-3" /> Suspend</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================ CONTENT ============================
function Content() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/videos").then((r) => setVideos(r.data));
  useEffect(() => { load(); }, []);
  const feature = async (v, f) => { await api.post(`/admin/videos/${v.id}/feature?featured=${f}`); load(); toast.success(f ? "Featured" : "Unfeatured"); };
  const unpublish = async (v) => { await api.post(`/admin/videos/${v.id}/unpublish`); load(); toast.success("Unpublished"); };
  const filtered = videos.filter((v) => !q || v.title?.toLowerCase().includes(q.toLowerCase()) || v.creator_name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/60" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search content…" className="bg-transparent outline-none text-sm text-white flex-1" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((v) => (
          <div key={v.id} className="tv-glass rounded-xl overflow-hidden">
            <div className="aspect-video bg-black"><img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /></div>
            <div className="p-4">
              <div className="font-display font-bold text-white line-clamp-1">{v.title}</div>
              <div className="text-xs text-white/50 mt-1">{v.creator_name} · {v.category} · {v.view_count?.toLocaleString()} views</div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={() => feature(v, !v.is_featured)} className="text-xs bg-tv-accent text-black font-bold px-3 py-1.5 rounded-full">{v.is_featured ? "Unfeature" : "Feature"}</button>
                {v.is_published && <button onClick={() => unpublish(v)} className="text-xs bg-red-500/80 text-white font-bold px-3 py-1.5 rounded-full">Unpublish</button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================ CATEGORIES ============================
function Categories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ slug: "", name: "", icon: "star" });
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/categories", form); toast.success("Category created"); setForm({ slug: "", name: "", icon: "star" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const remove = async (slug) => { if (!window.confirm(`Delete ${slug}?`)) return; await api.delete(`/admin/categories/${slug}`); toast.success("Deleted"); load(); };
  return (
    <div>
      <SectionTitle>Categories ({cats.length})</SectionTitle>
      <form onSubmit={create} className="tv-glass rounded-xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <input required placeholder="Slug (e.g. games)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" data-testid="cat-slug" />
        <input required placeholder="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" data-testid="cat-name" />
        <input placeholder="Icon (lucide name)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
        <button data-testid="cat-create" className="bg-tv-accent text-black font-bold rounded-full px-4"><Plus className="w-4 h-4 inline" /> Add Category</button>
      </form>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cats.map((c) => (
          <div key={c.slug} className="tv-glass rounded-xl p-4 flex items-center justify-between" data-testid={`cat-${c.slug}`}>
            <div>
              <div className="font-semibold text-white">{c.name}</div>
              <div className="text-xs text-white/40">/{c.slug}</div>
            </div>
            <button onClick={() => remove(c.slug)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================ ADS ============================
function Ads() {
  const [ads, setAds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", advertiser: "", image_url: "", click_url: "", placement: "pre_roll", budget_usd: 500 });
  const load = () => api.get("/admin/ads").then((r) => setAds(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    await api.post("/admin/ads", { ...form, kind: "platform" });
    toast.success("Ad created");
    setCreating(false);
    setForm({ title: "", advertiser: "", image_url: "", click_url: "", placement: "pre_roll", budget_usd: 500 });
    load();
  };
  const decide = async (a, decision) => { await api.post(`/admin/ads/${a.id}/decide?decision=${decision}`); toast.success(decision); load(); };
  const remove = async (a) => { if (!window.confirm("Delete ad?")) return; await api.delete(`/admin/ads/${a.id}`); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Ad Inventory ({ads.length})</SectionTitle>
        <button onClick={() => setCreating(!creating)} data-testid="ad-new-btn" className="bg-tv-accent text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New Ad</button>
      </div>
      {creating && (
        <form onSubmit={create} className="tv-glass rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <input required placeholder="Advertiser" value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <input placeholder="Click URL" value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <select value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} className="bg-[#0B1120] border border-white/10 rounded px-3 py-2 text-white">
            <option value="pre_roll">Pre-roll</option>
            <option value="mid_roll">Mid-roll</option>
            <option value="banner">Banner</option>
            <option value="sponsored">Sponsored</option>
          </select>
          <input type="number" placeholder="Budget (USD)" value={form.budget_usd} onChange={(e) => setForm({ ...form, budget_usd: Number(e.target.value) })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <button className="md:col-span-2 bg-tv-accent text-black font-bold rounded-full py-2.5">Create Ad</button>
        </form>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ads.map((a) => (
          <div key={a.id} data-testid={`ad-${a.id}`} className="tv-glass rounded-xl overflow-hidden">
            {a.image_url && <img src={a.image_url} alt="" className="w-full aspect-video object-cover" />}
            <div className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display font-bold text-white">{a.title}</div>
                  <div className="text-xs text-white/50">{a.advertiser} · {a.placement} · {a.kind}</div>
                </div>
                <Pill status={a.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div><div className="text-[10px] text-white/40 uppercase">Impressions</div><div className="font-bold text-white">{a.impressions?.toLocaleString()}</div></div>
                <div><div className="text-[10px] text-white/40 uppercase">Clicks</div><div className="font-bold text-white">{a.clicks?.toLocaleString()}</div></div>
                <div><div className="text-[10px] text-white/40 uppercase">Spend</div><div className="font-bold text-tv-accent">${a.spent_usd}</div></div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {a.status === "pending" && <button onClick={() => decide(a, "approve")} className="text-xs bg-tv-brand text-black font-bold px-3 py-1.5 rounded-full">Approve</button>}
                {a.status === "pending" && <button onClick={() => decide(a, "reject")} className="text-xs bg-red-500/80 text-white font-bold px-3 py-1.5 rounded-full">Reject</button>}
                {a.status === "approved" && <button onClick={() => decide(a, "pause")} className="text-xs bg-white/10 text-white font-bold px-3 py-1.5 rounded-full">Pause</button>}
                {a.status === "paused" && <button onClick={() => decide(a, "activate")} className="text-xs bg-green-500/80 text-white font-bold px-3 py-1.5 rounded-full">Activate</button>}
                <button onClick={() => remove(a)} className="text-xs text-red-400 ml-auto"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================ PAYOUTS ============================
function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => { const url = filter === "all" ? "/admin/payouts" : `/admin/payouts?status=${filter}`; api.get(url).then((r) => setPayouts(r.data)); };
  useEffect(() => { load(); }, [filter]);
  const pay = async (p) => { await api.post(`/admin/payouts/${p.id}/mark-paid`); toast.success("Marked paid"); load(); };
  const reject = async (p) => { const reason = window.prompt("Reject reason?") || ""; await api.post(`/admin/payouts/${p.id}/reject?reason=${encodeURIComponent(reason)}`); toast.success("Rejected"); load(); };
  const total = payouts.reduce((s, p) => s + (p.amount || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-2">
          {["pending", "paid", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ${filter === s ? "bg-white/20 text-white" : "text-white/60 hover:bg-white/5"}`}>{s}</button>
          ))}
        </div>
        <div className="tv-glass rounded-full px-4 py-1.5 text-sm text-white"><span className="text-white/60">Total in view:</span> <span className="font-bold text-tv-accent">${total.toFixed(2)}</span></div>
      </div>
      {payouts.length === 0 ? <Empty>No payouts in this bucket.</Empty> : (
        <div className="tv-glass rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-white/50">
              <th className="px-4 py-3">Creator</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Requested</th><th className="px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} data-testid={`payout-${p.id}`} className="border-t border-white/5">
                  <td className="px-4 py-3 text-white">{p.creator_name}<div className="text-xs text-white/50">{p.creator_email}</div></td>
                  <td className="px-4 py-3 font-bold text-tv-accent">${p.amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-white/70 uppercase text-xs">{p.method}</td>
                  <td className="px-4 py-3"><Pill status={p.status} /></td>
                  <td className="px-4 py-3 text-white/50 text-xs">{p.requested_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    {p.status === "pending" && <div className="flex gap-2">
                      <button onClick={() => pay(p)} data-testid={`pay-${p.id}`} className="text-xs bg-tv-brand text-black font-bold px-3 py-1 rounded">Mark Paid</button>
                      <button onClick={() => reject(p)} className="text-xs bg-red-500/80 text-white font-bold px-3 py-1 rounded">Reject</button>
                    </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ REVENUE ============================
function Revenue() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/revenue").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Stat label="Total revenue" value={`$${data.total_revenue_usd.toFixed(2)}`} icon={DollarSign} accent />
        <Stat label="Transactions" value={data.transactions_count} icon={FileText} />
        <Stat label="Tiers selling" value={Object.keys(data.by_tier).length} icon={Layers} />
      </div>
      <SectionTitle>By tier</SectionTitle>
      <div className="tv-glass rounded-xl p-5 space-y-2 mb-6">
        {Object.keys(data.by_tier).length === 0 ? <Empty>No completed transactions yet.</Empty> :
          Object.entries(data.by_tier).map(([tier, amt]) => (
            <div key={tier} className="flex items-center justify-between">
              <div className="text-white uppercase text-xs tracking-wider">{tier.replace("_", " ")}</div>
              <div className="font-bold text-tv-accent">${amt.toFixed(2)}</div>
            </div>
          ))}
      </div>
      <SectionTitle>Recent transactions</SectionTitle>
      {data.recent.length === 0 ? <Empty>No transactions yet.</Empty> : (
        <div className="space-y-2">
          {data.recent.map((t, i) => (
            <div key={i} className="tv-glass rounded-lg p-3 flex items-center justify-between">
              <div>
                <div className="text-white font-semibold">{t.email}</div>
                <div className="text-xs text-white/50">{t.tier} · {t.created_at?.slice(0, 10)}</div>
              </div>
              <div className="font-bold text-tv-accent">${t.amount?.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ LIVE OPS ============================
function LiveOps() {
  const [streams, setStreams] = useState([]);
  const load = () => api.get("/admin/live").then((r) => setStreams(r.data));
  useEffect(() => { load(); const h = setInterval(load, 5000); return () => clearInterval(h); }, []);
  const forceStop = async (s) => {
    const reason = window.prompt("Stop reason?", "Policy violation");
    if (reason == null) return;
    await api.post(`/admin/live/${s.id}/force-stop?reason=${encodeURIComponent(reason)}`);
    toast.success("Stream stopped");
    load();
  };
  return (
    <div>
      <SectionTitle>Active live streams ({streams.length})</SectionTitle>
      {streams.length === 0 ? <Empty>No active live streams.</Empty> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {streams.map((s) => (
            <div key={s.id} data-testid={`live-${s.id}`} className="tv-glass rounded-xl overflow-hidden">
              <div className="aspect-video bg-black relative">
                {s.thumbnail_url && <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover opacity-80" />}
                <div className="absolute top-3 left-3 bg-tv-live text-white text-[10px] font-bold uppercase px-2 py-1 rounded">LIVE</div>
              </div>
              <div className="p-4">
                <div className="font-display font-bold text-white">{s.title}</div>
                <div className="text-xs text-white/50 mt-1">{s.creator_name} · {s.viewer_count?.toLocaleString()} viewers · scene: {s.scene}</div>
                <button onClick={() => forceStop(s)} data-testid={`force-stop-${s.id}`} className="mt-3 bg-red-500/80 hover:bg-red-600 text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2"><StopCircle className="w-4 h-4" /> Force stop</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ REPORTS ============================
function Reports() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("open");
  const load = () => { const url = filter === "all" ? "/admin/reports" : `/admin/reports?status=${filter}`; api.get(url).then((r) => setReports(r.data)); };
  useEffect(() => { load(); }, [filter]);
  const resolve = async (r, action) => {
    const note = window.prompt("Resolution note?", "") || "";
    await api.post(`/admin/reports/${r.id}/resolve?action=${action}&note=${encodeURIComponent(note)}`);
    toast.success("Resolved");
    load();
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["open", "resolved", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ${filter === s ? "bg-white/20 text-white" : "text-white/60 hover:bg-white/5"}`}>{s}</button>
        ))}
      </div>
      {reports.length === 0 ? <Empty>No reports in this bucket.</Empty> : (
        <div className="space-y-3">
          {reports.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="tv-glass rounded-xl p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display font-bold text-white">{r.reason}</div>
                  <div className="text-xs text-white/50">on {r.target_type} · {r.target_title || r.target_id}</div>
                </div>
                <Pill status={r.status} />
              </div>
              <div className="text-sm text-white/80 mt-2">{r.details}</div>
              <div className="text-xs text-white/40 mt-1">By {r.reporter_email} · {r.created_at?.slice(0, 16).replace("T", " ")}</div>
              {r.status === "open" && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => resolve(r, "takedown")} data-testid={`resolve-takedown-${r.id}`} className="text-xs bg-red-500/80 text-white font-bold px-3 py-1.5 rounded-full">Takedown</button>
                  <button onClick={() => resolve(r, "warning")} className="text-xs bg-yellow-500/80 text-black font-bold px-3 py-1.5 rounded-full">Warn creator</button>
                  <button onClick={() => resolve(r, "dismiss")} className="text-xs bg-white/10 text-white font-bold px-3 py-1.5 rounded-full">Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ ANALYTICS ============================
function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics/network").then((r) => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <SectionTitle>Top videos (by views)</SectionTitle>
        <div className="space-y-2">
          {data.top_videos.map((v) => (
            <div key={v.id} className="tv-glass rounded-lg p-3 flex items-center gap-3">
              <img src={v.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded" />
              <div className="flex-1 min-w-0"><div className="text-white font-semibold truncate">{v.title}</div><div className="text-xs text-white/50">{v.creator_name}</div></div>
              <div className="font-bold text-tv-accent">{v.view_count?.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <SectionTitle>By category</SectionTitle>
        <div className="space-y-2">
          {data.by_category.map((c) => {
            const max = Math.max(...data.by_category.map((x) => x.views));
            const pct = max ? (c.views / max) * 100 : 0;
            return (
              <div key={c.category} className="tv-glass rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <div className="text-white font-semibold uppercase text-xs tracking-wider">{c.category}</div>
                  <div className="text-white/60">{c.videos} vids · {c.views.toLocaleString()} views</div>
                </div>
                <div className="tv-progress mt-2"><div style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================ ANNOUNCEMENTS ============================
function Announcements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", kind: "info" });
  const load = () => api.get("/admin/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => { e.preventDefault(); await api.post("/admin/announcements", form); toast.success("Posted"); setForm({ title: "", message: "", kind: "info" }); load(); };
  const remove = async (a) => { await api.delete(`/admin/announcements/${a.id}`); load(); };
  return (
    <div>
      <SectionTitle>Network announcements</SectionTitle>
      <form onSubmit={create} className="tv-glass rounded-xl p-5 space-y-3 mb-6">
        <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white" data-testid="ann-title" />
        <textarea required rows={2} placeholder="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white" data-testid="ann-message" />
        <div className="flex items-center gap-3">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="bg-[#0B1120] border border-white/10 rounded px-3 py-2 text-white">
            <option value="info">Info</option><option value="success">Success</option><option value="warning">Warning</option>
          </select>
          <button data-testid="ann-publish" className="bg-tv-accent text-black font-bold rounded-full px-5 py-2">Publish</button>
        </div>
      </form>
      {items.length === 0 ? <Empty>No announcements.</Empty> : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="tv-glass rounded-xl p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-display font-bold text-white">{a.title}</div>
                  <div className="text-sm text-white/70 mt-1">{a.message}</div>
                </div>
                <button onClick={() => remove(a)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="text-xs text-white/40 mt-2 flex items-center gap-2"><Pill status={a.active ? "active" : "paused"} /> · {a.kind} · {a.created_at?.slice(0, 10)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ AUDIT ============================
function AuditLog() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setEntries(r.data)); }, []);
  return (
    <div>
      <SectionTitle>Admin audit log ({entries.length})</SectionTitle>
      {entries.length === 0 ? <Empty>No admin actions logged yet.</Empty> : (
        <div className="tv-glass rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-white/50">
              <th className="px-4 py-3">When</th><th className="px-4 py-3">Admin</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th>
            </tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-white/5">
                  <td className="px-4 py-3 text-white/60 text-xs">{e.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-white">{e.admin_email}</td>
                  <td className="px-4 py-3 text-tv-brand text-xs uppercase">{e.action}</td>
                  <td className="px-4 py-3 text-white/60 text-xs">{e.target_type}: {e.target_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ SETTINGS ============================
function Settings() {
  const [settings, setSettings] = useState({});
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/settings").then((r) => {
    setSettings(r.data);
    setRows(Object.entries(r.data).map(([k, v]) => ({ key: k, value: v })));
  });
  useEffect(() => { load(); }, []);
  const save = async (key, value, isPublic) => {
    await api.post("/admin/settings", { key, value, public: isPublic });
    toast.success(`${key} saved`);
    load();
  };
  return (
    <div>
      <SectionTitle>Network settings</SectionTitle>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={r.key} className="tv-glass rounded-xl p-4 grid md:grid-cols-[200px_1fr_auto] gap-3 items-center">
            <div>
              <div className="text-white font-semibold">{r.key}</div>
              <div className="text-xs text-white/40">current: {String(settings[r.key])}</div>
            </div>
            <input
              value={r.value ?? ""}
              onChange={(e) => { const next = [...rows]; next[i] = { ...r, value: e.target.value }; setRows(next); }}
              className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white"
            />
            <button onClick={() => save(r.key, r.value, true)} className="bg-tv-accent text-black font-bold rounded-full px-4 py-2 text-sm">Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================ REAL-TIME METRICS (WebSocket) ============================
function RealtimeMetrics() {
  const [data, setData] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const history = useRef([]);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, "ws") + "/api/ws/admin-metrics";
    let stopped = false;
    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onmessage = (e) => {
          const d = JSON.parse(e.data);
          setData(d);
          history.current = [...history.current.slice(-19), d];
        };
        ws.onclose = () => {
          setConnected(false);
          if (!stopped) setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch { setTimeout(connect, 3000); }
    };
    connect();
    return () => { stopped = true; try { wsRef.current?.close(); } catch {} };
  }, []);

  return (
    <div data-testid="realtime-tab">
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-2.5 h-2.5 rounded-full ${connected ? "bg-green-400 tv-pulse" : "bg-red-400"}`} />
        <div className="text-sm text-white/70">{connected ? "WebSocket connected · live" : "Connecting…"}</div>
      </div>
      {!data ? <Empty>Waiting for first tick…</Empty> : (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Live streams" value={data.live_now} icon={Radio} accent />
            <Stat label="Concurrent viewers" value={data.concurrent_viewers?.toLocaleString()} icon={Eye} />
            <Stat label="Total users" value={data.users?.toLocaleString()} icon={Users} />
            <Stat label="Revenue" value={`$${data.total_revenue?.toLocaleString()}`} icon={DollarSign} accent />
          </div>
          <SectionTitle>Tick history (last 20)</SectionTitle>
          <div className="tv-glass rounded-xl p-5 flex items-end gap-1 h-40">
            {history.current.map((t, i) => {
              const max = Math.max(...history.current.map((x) => x.concurrent_viewers || 1));
              const h = ((t.concurrent_viewers || 0) / max) * 100;
              return <div key={i} style={{ height: `${h}%` }} className="flex-1 bg-tv-brand/80 rounded-t" title={`${t.concurrent_viewers} viewers`} />;
            })}
          </div>
          <div className="mt-2 text-xs text-white/40">Last tick: {data.ts?.slice(11, 19)} UTC</div>
        </div>
      )}
    </div>
  );
}

// ============================ PARTNERS ============================
function Partners() {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ slug: "", name: "", tagline: "", description: "", logo_url: "", banner_url: "", brand_color: "#38BDF8" });
  const load = () => api.get("/partners").then((r) => setPartners(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/partners", form); toast.success("Partner created"); setForm({ slug: "", name: "", tagline: "", description: "", logo_url: "", banner_url: "", brand_color: "#38BDF8" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <SectionTitle>Partner channels</SectionTitle>
      <form onSubmit={create} className="tv-glass rounded-xl p-5 grid md:grid-cols-2 gap-3 mb-6">
        <input required placeholder="Slug (url-friendly)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" data-testid="partner-slug" />
        <input required placeholder="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
        <input placeholder="Tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="md:col-span-2 bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
        <input placeholder="Logo URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
        <input placeholder="Banner URL" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
        <input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 bg-white/5 border border-white/10 rounded" />
        <button className="md:col-span-2 bg-tv-accent text-black font-bold rounded-full py-2.5">Create Partner Channel</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {partners.map((p) => (
          <div key={p.slug} className="tv-glass rounded-xl overflow-hidden" data-testid={`partner-${p.slug}`}>
            <div className="aspect-video bg-black relative">
              {p.banner_url && <img src={p.banner_url} alt="" className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-3">
                {p.logo_url && <img src={p.logo_url} className="w-14 h-14 rounded-lg object-cover border border-white/20" alt="" />}
                <div>
                  <div className="font-display font-black text-xl text-white">{p.name}</div>
                  <div className="text-xs text-white/70">/{p.slug}</div>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm text-white/80">{p.tagline}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded" style={{ background: p.brand_color }} />
                <span className="text-xs text-white/40">{p.brand_color}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================ DMCA / STRIKES ============================
function DMCA() {
  const [notices, setNotices] = useState([]);
  const [filter, setFilter] = useState("open");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ video_id: "", claimant: "", reason: "" });
  const load = () => { const url = filter === "all" ? "/admin/dmca" : `/admin/dmca?status=${filter}`; api.get(url).then((r) => setNotices(r.data)); };
  useEffect(() => { load(); }, [filter]);
  const file = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/dmca", form); toast.success("DMCA filed"); setCreating(false); setForm({ video_id: "", claimant: "", reason: "" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const resolve = async (n, decision) => {
    await api.post(`/admin/dmca/${n.id}/resolve?decision=${decision}`);
    toast.success(decision === "uphold" ? "Upheld — strike issued" : decision);
    load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-2">
          {["open", "upheld", "countered", "retracted", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider ${filter === s ? "bg-white/20 text-white" : "text-white/60 hover:bg-white/5"}`}>{s}</button>
          ))}
        </div>
        <button onClick={() => setCreating(!creating)} className="bg-tv-accent text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2" data-testid="dmca-new-btn"><Plus className="w-4 h-4" /> File Notice</button>
      </div>
      {creating && (
        <form onSubmit={file} className="tv-glass rounded-xl p-5 space-y-3 mb-6">
          <input required placeholder="Video ID" value={form.video_id} onChange={(e) => setForm({ ...form, video_id: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <input required placeholder="Claimant (e.g. Sony Music)" value={form.claimant} onChange={(e) => setForm({ ...form, claimant: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <textarea required rows={2} placeholder="Reason / cited work" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <button className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full">Submit</button>
        </form>
      )}
      <div className="mb-4 tv-glass rounded-xl p-4 text-sm text-white/70 flex items-center gap-3">
        <Shield className="w-5 h-5 text-tv-live" />
        <div>
          <div className="text-white font-semibold">3-strikes policy active</div>
          <div>Creators accrue copyright strikes that expire after 90 days. 3 active strikes → auto-suspension.</div>
        </div>
      </div>
      {notices.length === 0 ? <Empty>No DMCA notices.</Empty> : (
        <div className="space-y-3">
          {notices.map((n) => (
            <div key={n.id} data-testid={`dmca-${n.id}`} className="tv-glass rounded-xl p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display font-bold text-white">{n.video_title}</div>
                  <div className="text-xs text-white/50">Claim by {n.claimant} · {n.claim_type}{n.match_score ? ` · ${n.match_score}% match` : ""}</div>
                </div>
                <Pill status={n.status === "upheld" ? "denied" : n.status === "open" ? "open" : "resolved"} />
              </div>
              <div className="text-sm text-white/80 mt-2">{n.reason}</div>
              <div className="text-xs text-white/40 mt-1">Creator: {n.creator_email}</div>
              {n.status === "open" && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => resolve(n, "uphold")} data-testid={`dmca-uphold-${n.id}`} className="text-xs bg-red-500/80 text-white font-bold px-3 py-1.5 rounded-full">Uphold (+Strike, Unpublish)</button>
                  <button onClick={() => resolve(n, "counter")} className="text-xs bg-white/10 text-white font-bold px-3 py-1.5 rounded-full">Counter-notice valid</button>
                  <button onClick={() => resolve(n, "retract")} className="text-xs bg-white/10 text-white/60 font-bold px-3 py-1.5 rounded-full">Retract</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ TAX FORMS ============================
function TaxForms() {
  const [forms, setForms] = useState([]);
  const load = () => api.get("/admin/tax-forms").then((r) => setForms(Array.isArray(r.data) ? r.data : (r.data?.forms || [])));
  useEffect(() => { load(); }, []);
  const decide = async (f, decision) => { await api.post(`/admin/tax-forms/${f.user_id}/decide?decision=${decision}`); toast.success(decision); load(); };
  return (
    <div>
      <div className="tv-glass rounded-xl p-4 text-sm text-white/70 mb-4 flex items-center gap-3">
        <Receipt className="w-5 h-5 text-tv-accent" />
        <div>
          <div className="text-white font-semibold">US Creator Tax Compliance</div>
          <div>Creators must submit W-9 (US) or W-8BEN (non-US) before payouts are released. IRS 1099-NEC issued at $600+.</div>
        </div>
      </div>
      <SectionTitle>Submitted tax forms ({forms.length})</SectionTitle>
      {forms.length === 0 ? <Empty>No tax forms submitted yet.</Empty> : (
        <div className="tv-glass rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-white/50">
              <th className="px-4 py-3">Creator</th><th className="px-4 py-3">Form</th><th className="px-4 py-3">TIN</th>
              <th className="px-4 py-3">Country</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
            </tr></thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.user_id} data-testid={`tax-${f.user_id}`} className="border-t border-white/5">
                  <td className="px-4 py-3 text-white">{f.legal_name}<div className="text-xs text-white/50">{f.user_email}</div></td>
                  <td className="px-4 py-3 text-tv-brand uppercase text-xs">{f.form_type}</td>
                  <td className="px-4 py-3 text-white/60 font-mono">••••{f.tin_last4 || "----"}</td>
                  <td className="px-4 py-3 text-white/70 uppercase text-xs">{f.country}</td>
                  <td className="px-4 py-3"><Pill status={f.status === "verified" ? "approved" : f.status === "rejected" ? "rejected" : "pending"} /></td>
                  <td className="px-4 py-3">
                    {f.status === "pending_review" && <div className="flex gap-2">
                      <button onClick={() => decide(f, "approve")} data-testid={`tax-approve-${f.user_id}`} className="text-xs bg-tv-brand text-black font-bold px-3 py-1 rounded">Verify</button>
                      <button onClick={() => decide(f, "reject")} className="text-xs bg-red-500/80 text-white font-bold px-3 py-1 rounded">Reject</button>
                    </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================ VERIFICATION ============================
function Verification() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role === "creator" || u.role === "premium_creator")));
  useEffect(() => { load(); }, []);
  const verify = async (u, v) => { await api.post(`/admin/users/${u.user_id}/verify?verified=${v}`); toast.success(v ? "Verified ✓" : "Unverified"); load(); };
  const adPartner = async (u, v) => { await api.post(`/admin/users/${u.user_id}/ad-partner?verified=${v}`); toast.success(v ? "Ad Partner ✓" : "Removed"); load(); };
  const filtered = users.filter((u) => !q || u.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/60" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators…" className="bg-transparent outline-none text-sm text-white flex-1" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((u) => (
          <div key={u.user_id} data-testid={`verify-${u.user_id}`} className="tv-glass rounded-xl p-4 flex items-center gap-4">
            <img src={u.picture} className="w-14 h-14 rounded-full object-cover" alt="" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-display font-bold text-white truncate">{u.name}</div>
                {u.is_verified && <CheckCircle2 className="w-4 h-4 text-tv-brand" />}
                {u.verified_ad_partner && <Award className="w-4 h-4 text-tv-accent" />}
              </div>
              <div className="text-xs text-white/50">{u.email} · {u.role}</div>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => verify(u, !u.is_verified)} className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${u.is_verified ? "bg-tv-brand/20 text-tv-brand" : "bg-white/10 text-white"}`}>
                {u.is_verified ? "✓ Verified" : "Verify"}
              </button>
              <button onClick={() => adPartner(u, !u.verified_ad_partner)} className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${u.verified_ad_partner ? "bg-tv-accent/20 text-tv-accent" : "bg-white/10 text-white"}`}>
                {u.verified_ad_partner ? "★ Ad Partner" : "Ad Partner"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================ EXPERIMENTS ============================
function Experiments() {
  const [exps, setExps] = useState([]);
  const [videos, setVideos] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", variants: [] });
  const [pickIds, setPickIds] = useState(["", ""]);
  const load = async () => {
    const r = await api.get("/admin/experiments");
    setExps(r.data);
    const v = await api.get("/admin/videos");
    setVideos(v.data.slice(0, 20));
  };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    const variants = pickIds.filter(Boolean).map((id) => ({ video_id: id, label: videos.find((v) => v.id === id)?.title || id }));
    if (variants.length < 2) return toast.error("Pick at least 2 videos");
    await api.post("/admin/experiments", { name: form.name, variants, auto_promote: true });
    toast.success("Experiment started");
    setCreating(false); setForm({ name: "", variants: [] }); setPickIds(["", ""]);
    load();
  };
  const conclude = async (e) => {
    const r = await api.post(`/admin/experiments/${e.id}/conclude`);
    toast.success(`Winner: ${r.data.winner || "none"}`);
    load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>Hero banner A/B tests</SectionTitle>
        <button onClick={() => setCreating(!creating)} className="bg-tv-accent text-black font-bold px-4 py-2 rounded-full text-sm flex items-center gap-2" data-testid="exp-new-btn"><Plus className="w-4 h-4" /> New Test</button>
      </div>
      {creating && (
        <form onSubmit={create} className="tv-glass rounded-xl p-5 space-y-3 mb-6">
          <input required placeholder="Experiment name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          {[0, 1, 2].map((i) => (
            <select key={i} value={pickIds[i] || ""} onChange={(e) => { const next = [...pickIds]; next[i] = e.target.value; setPickIds(next); }} className="w-full bg-[#0B1120] border border-white/10 rounded px-3 py-2 text-white">
              <option value="">Variant {String.fromCharCode(65 + i)} — pick video{i < 2 ? " (required)" : " (optional)"}</option>
              {videos.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
            </select>
          ))}
          <button className="bg-tv-accent text-black font-bold rounded-full px-5 py-2">Start A/B Test</button>
        </form>
      )}
      {exps.length === 0 ? <Empty>No experiments yet.</Empty> : (
        <div className="space-y-3">
          {exps.map((e) => {
            const impressions = e.impressions || {};
            const clicks = e.clicks || {};
            return (
              <div key={e.id} data-testid={`exp-${e.id}`} className="tv-glass rounded-xl p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-display font-bold text-white">{e.name}</div>
                    <div className="text-xs text-white/50">{e.variants?.length} variants · status {e.status}</div>
                  </div>
                  {e.status === "running" && <button onClick={() => conclude(e)} className="text-xs bg-tv-accent text-black font-bold px-3 py-1.5 rounded-full">Conclude & Promote Winner</button>}
                  {e.winner && <div className="text-xs text-tv-brand font-bold">Winner: {e.winner} ({(e.winner_ctr * 100).toFixed(2)}% CTR)</div>}
                </div>
                <div className="mt-3 space-y-2">
                  {e.variants?.map((v) => {
                    const imp = impressions[v.video_id] || 0;
                    const cl = clicks[v.video_id] || 0;
                    const ctr = imp ? ((cl / imp) * 100).toFixed(2) : "0.00";
                    return (
                      <div key={v.video_id} className="flex items-center justify-between text-sm">
                        <div className="text-white truncate">{v.label}</div>
                        <div className="text-xs text-white/60">{imp} impr · {cl} clicks · <span className="text-tv-accent font-bold">{ctr}%</span></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================ TRENDING TUNER ============================
function TrendingTuner() {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/admin/trending-weights").then((r) => setW(r.data)); }, []);
  if (!w) return null;
  const save = async () => {
    const total = (w.views || 0) + (w.recency || 0) + (w.likes || 0) + (w.completion || 0);
    if (Math.abs(total - 1) > 0.01) return toast.error(`Weights must sum to 1.0 (current: ${total.toFixed(2)})`);
    await api.post("/admin/trending-weights", w);
    toast.success("Weights saved");
  };
  const Slider = ({ keyName, label }) => (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-white">{label}</span>
        <span className="text-tv-accent font-bold">{((w[keyName] || 0) * 100).toFixed(0)}%</span>
      </div>
      <input
        type="range" min="0" max="1" step="0.05" value={w[keyName] || 0}
        onChange={(e) => setW({ ...w, [keyName]: parseFloat(e.target.value) })}
        className="w-full accent-tv-accent"
        data-testid={`weight-${keyName}`}
      />
    </div>
  );
  return (
    <div className="max-w-2xl">
      <SectionTitle>Trending algorithm weights</SectionTitle>
      <div className="tv-glass rounded-xl p-6 space-y-5">
        <Slider keyName="views" label="Views weight" />
        <Slider keyName="recency" label="Recency weight" />
        <Slider keyName="likes" label="Likes weight" />
        <Slider keyName="completion" label="Completion rate weight" />
        <div className="flex items-center justify-between pt-3 border-t border-white/10">
          <div className="text-sm text-white/70">Must sum to <span className="text-white font-bold">1.00</span></div>
          <button onClick={save} className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full">Save weights</button>
        </div>
      </div>
    </div>
  );
}



// =========================================================
// HERO SLIDES — Admin-managed rotating banner
// =========================================================
function HeroSlidesTab() {
  const [slides, setSlides] = useState([]);
  const [draft, setDraft] = useState(null); // null = hidden; {} = new; {...slide} = edit

  const load = async () => {
    const r = await api.get("/admin/hero-slides");
    setSlides(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const uploadAsset = async (file, kind) => {
    if (!file) return null;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post(kind === "video" ? "/uploads/video" : "/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.storage_path}`;
  };

  const save = async (form, imgFile, vidFile) => {
    let { image_url, trailer_url } = form;
    if (imgFile) image_url = await uploadAsset(imgFile, "image");
    if (vidFile) trailer_url = await uploadAsset(vidFile, "video");
    const body = { ...form, image_url, trailer_url };
    if (form.id) {
      await api.put(`/admin/hero-slides/${form.id}`, body);
      toast.success("Slide updated");
    } else {
      await api.post("/admin/hero-slides", body);
      toast.success("Slide created");
    }
    setDraft(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    await api.delete(`/admin/hero-slides/${id}`);
    toast.info("Slide deleted");
    load();
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/hero-slides/${s.id}`, { is_active: !s.is_active });
    load();
  };

  return (
    <div data-testid="admin-hero-slides">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle>Hero Slides</SectionTitle>
        <button data-testid="hero-slide-new-btn" onClick={() => setDraft({ title: "", subtitle: "", description: "", image_url: "", trailer_url: "", watch_url: "", info_url: "", badge: "", is_active: true, order: slides.length, target: "both" })} className="bg-tv-accent text-black font-bold px-4 py-2 rounded-full flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Slide
        </button>
      </div>

      {draft && <HeroSlideEditor initial={draft} onCancel={() => setDraft(null)} onSave={save} />}

      <div className="space-y-3 mt-4">
        {slides.length === 0 && <div className="text-white/60">No slides yet. Click "New Slide".</div>}
        {slides.map((s) => (
          <div key={s.id} data-testid={`hero-slide-${s.id}`} className="tv-glass rounded-xl p-4 flex items-center gap-4">
            <img src={s.image_url || "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=400&q=80"} alt="" className="w-32 h-18 object-cover rounded" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {s.badge && <span className="bg-tv-brand text-black text-[10px] font-bold uppercase px-2 py-0.5 rounded">{s.badge}</span>}
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${s.is_active ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/50"}`}>{s.is_active ? "Active" : "Off"}</span>
                <span
                  className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                    (s.target || "both") === "tv"
                      ? "bg-fuchsia-500/20 text-fuchsia-200"
                      : (s.target || "both") === "web"
                      ? "bg-sky-500/20 text-sky-200"
                      : "bg-amber-400/20 text-amber-200"
                  }`}
                  data-testid={`hero-slide-target-${s.id}`}
                  title="Visible on"
                >
                  {(s.target || "both") === "tv" ? "TV" : (s.target || "both") === "web" ? "Web" : "Web + TV"}
                </span>
                <span className="text-xs text-white/50">order {s.order}</span>
              </div>
              <div className="font-display font-bold text-white text-lg truncate">{s.title}</div>
              <div className="text-xs text-white/50 truncate">{s.description}</div>
            </div>
            <button data-testid={`hero-slide-toggle-${s.id}`} onClick={() => toggleActive(s)} className="text-xs px-3 py-1.5 rounded-full tv-glass text-white border border-white/15">
              {s.is_active ? "Disable" : "Enable"}
            </button>
            <button data-testid={`hero-slide-edit-${s.id}`} onClick={() => setDraft(s)} className="text-xs px-3 py-1.5 rounded-full bg-white/10 text-white">Edit</button>
            <button data-testid={`hero-slide-delete-${s.id}`} onClick={() => remove(s.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroSlideEditor({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const [imgFile, setImgFile] = useState(null);
  const [vidFile, setVidFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(form, imgFile, vidFile); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="hero-slide-editor" className="tv-glass rounded-2xl p-5 space-y-3 border border-tv-accent/30">
      <div className="grid md:grid-cols-2 gap-3">
        <input data-testid="hero-slide-title" placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" required />
        <input data-testid="hero-slide-subtitle" placeholder="Subtitle (e.g. PREMIERE)" value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
      </div>
      <textarea data-testid="hero-slide-desc" placeholder="Description" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Image URL</label>
          <input data-testid="hero-slide-image-url" placeholder="https://…" value={form.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          <div className="text-xs text-white/50 mt-1">or upload from device:</div>
          <input data-testid="hero-slide-image-file" type="file" accept="image/*" onChange={(e) => setImgFile(e.target.files?.[0])} className="text-xs text-white/70 mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Trailer video URL (optional)</label>
          <input data-testid="hero-slide-trailer-url" placeholder="https://…/trailer.mp4" value={form.trailer_url || ""} onChange={(e) => set("trailer_url", e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          <div className="text-xs text-white/50 mt-1">or upload from device:</div>
          <input data-testid="hero-slide-trailer-file" type="file" accept="video/*" onChange={(e) => setVidFile(e.target.files?.[0])} className="text-xs text-white/70 mt-1" />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <input data-testid="hero-slide-watch-url" placeholder="Watch Live URL (e.g. /live/:id)" value={form.watch_url || ""} onChange={(e) => set("watch_url", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="hero-slide-info-url" placeholder="More Info URL" value={form.info_url || ""} onChange={(e) => set("info_url", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="hero-slide-badge" placeholder="Badge (LIVE / NEW)" value={form.badge || ""} onChange={(e) => set("badge", e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <label className="text-xs text-white/70 flex items-center gap-2">Order
          <input data-testid="hero-slide-order" type="number" value={form.order ?? 0} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} className="w-20 bg-white/5 border border-white/10 rounded px-2 py-1 text-white" />
        </label>
        <label className="text-xs text-white/70 flex items-center gap-2">Show on
          <select
            data-testid="hero-slide-target"
            value={form.target || "both"}
            onChange={(e) => set("target", e.target.value)}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white"
          >
            <option value="both">Web + TV</option>
            <option value="web">Web only</option>
            <option value="tv">TV only</option>
          </select>
        </label>
        <label className="text-xs text-white/70 flex items-center gap-2">
          <input data-testid="hero-slide-active" type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Active
        </label>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button type="button" onClick={onCancel} className="text-white/70 px-4 py-2">Cancel</button>
        <button data-testid="hero-slide-save-btn" disabled={busy} type="submit" className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full disabled:opacity-60">{busy ? "Saving…" : "Save Slide"}</button>
      </div>
    </form>
  );
}

// =========================================================
// KIDS QUEUE — pending admin approval
// =========================================================
function KidsQueueTab() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/kids/pending");
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const approve = async (vid, featureOnFamily) => {
    await api.post(`/admin/kids/videos/${vid}/approve`, { feature_on_family: featureOnFamily });
    toast.success(featureOnFamily ? "Approved + Featured on Family" : "Approved");
    load();
  };
  const reject = async (vid) => {
    const reason = window.prompt("Reason for rejection (optional)") || "";
    await api.post(`/admin/kids/videos/${vid}/reject`, { reason });
    toast.info("Rejected");
    load();
  };

  return (
    <div data-testid="admin-kids-queue">
      <SectionTitle>Kids Queue — parent-approved, awaiting admin</SectionTitle>
      {items.length === 0 ? <div className="text-white/60 mt-4">No kid videos awaiting review.</div> : (
        <div className="space-y-3 mt-4">
          {items.map((v) => (
            <div key={v.id} className="tv-glass rounded-xl p-4 flex items-center gap-4" data-testid={`admin-kids-item-${v.id}`}>
              {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-32 h-20 object-cover rounded" />}
              <div className="flex-1 min-w-0">
                <div className="text-white font-display font-bold text-lg truncate">{v.title}</div>
                <div className="text-xs text-white/50 truncate">{v.creator_name} · {v.category}</div>
              </div>
              <button data-testid={`admin-kids-approve-${v.id}`} onClick={() => approve(v.id, false)} className="bg-green-500 hover:bg-green-600 text-white font-bold px-3 py-1.5 rounded-full text-sm">Approve</button>
              <button data-testid={`admin-kids-family-${v.id}`} onClick={() => approve(v.id, true)} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-full text-sm">Approve + Family</button>
              <button data-testid={`admin-kids-reject-${v.id}`} onClick={() => reject(v.id)} className="bg-red-500/90 hover:bg-red-600 text-white font-bold px-3 py-1.5 rounded-full text-sm">Reject</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// AWARDS admin — season + top leaderboard
// =========================================================
function AwardsAdminTab() {
  const [season, setSeason] = useState(null);
  const [board, setBoard] = useState([]);
  const [form, setForm] = useState({ name: "", prize_pool_usd: 50000, start_date: "", end_date: "" });

  const load = async () => {
    const s = await api.get("/awards/season"); setSeason(s.data); setForm({
      id: s.data.id, name: s.data.name, prize_pool_usd: s.data.prize_pool_usd,
      start_date: s.data.start_date, end_date: s.data.end_date,
    });
    const b = await api.get("/awards/leaderboard?limit=25"); setBoard(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    await api.put("/admin/awards/season", { ...form, prize_pool_usd: Number(form.prize_pool_usd) });
    toast.success("Season updated");
    load();
  };

  return (
    <div data-testid="admin-awards-tab">
      <SectionTitle>Creator Awards Season</SectionTitle>
      <form onSubmit={save} className="tv-glass rounded-2xl p-5 grid md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Season name</label>
          <input data-testid="awards-season-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Prize pool (USD)</label>
          <input data-testid="awards-season-prize" type="number" value={form.prize_pool_usd} onChange={(e) => setForm({ ...form, prize_pool_usd: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Start</label>
          <input data-testid="awards-season-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">End</label>
          <input data-testid="awards-season-end" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        </div>
        <div className="md:col-span-4 flex justify-end">
          <button data-testid="awards-season-save" type="submit" className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full">Save Season</button>
        </div>
      </form>

      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <AwardsStat label="Total votes" value={(season?.total_votes ?? 0).toLocaleString()} />
        <AwardsStat label="Unique voters" value={(season?.unique_voters ?? 0).toLocaleString()} />
        <AwardsStat label="Prize pool" value={`$${(season?.prize_pool_usd || 0).toLocaleString()}`} />
      </div>

      <div className="mt-6">
        <SectionTitle>Leaderboard</SectionTitle>
        <div className="space-y-2 mt-3">
          {board.length === 0 ? <div className="text-white/60">No votes yet.</div> : board.map((v, i) => (
            <div key={v.id} className="tv-glass rounded-xl p-3 flex items-center gap-3">
              <div className={`font-display font-black text-2xl w-8 text-center ${i === 0 ? "text-tv-accent" : "text-white/50"}`}>#{i + 1}</div>
              <img src={v.thumbnail_url} alt="" className="w-20 h-12 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{v.title}</div>
                <div className="text-xs text-white/50 truncate">{v.creator_name}</div>
              </div>
              <div className="text-right"><div className="text-tv-accent font-black text-xl">{v.vote_count}</div><div className="text-[10px] uppercase text-white/50">votes</div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AwardsStat({ label, value }) {
  return (
    <div className="tv-glass rounded-xl p-4">
      <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">{label}</div>
      <div className="font-display font-black text-2xl text-white mt-1">{value}</div>
    </div>
  );
}


// =========================================================
// PAYMENTS — Stripe transactions dashboard
// =========================================================
function PaymentsAdminTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    const q = filter ? `?status=${filter}&limit=100` : "?limit=100";
    const r = await api.get(`/admin/payments/transactions${q}`);
    setData(r.data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  if (!data) return <div className="text-white/60">Loading…</div>;
  const s = data.summary;

  return (
    <div data-testid="admin-payments-tab">
      <SectionTitle>Payments — Stripe transactions</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <AwardsStat label="Paid subs" value={s.paid_count.toLocaleString()} />
        <AwardsStat label="Subs revenue" value={`$${s.paid_amount_usd.toLocaleString()}`} />
        <AwardsStat label="Pending checkouts" value={s.initiated_count.toLocaleString()} />
        <AwardsStat label="Creator tips paid" value={`${s.tips_paid_count} · $${s.tips_paid_amount_usd.toLocaleString()}`} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs uppercase tracking-wider text-white/50 font-semibold">Filter:</span>
        {["", "paid", "initiated"].map((f) => (
          <button
            key={f || "all"}
            data-testid={`payments-filter-${f || "all"}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === f ? "bg-tv-accent text-black" : "tv-glass text-white/80 border border-white/10"}`}
          >
            {f || "All"}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {data.transactions.length === 0 ? (
          <div className="text-white/60" data-testid="payments-empty">No transactions yet.</div>
        ) : data.transactions.map((t) => (
          <div key={t.id} data-testid={`payment-txn-${t.id}`} className="tv-glass rounded-xl p-3 flex items-center gap-4 text-sm">
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold truncate">{t.email || t.user_id}</div>
              <div className="text-xs text-white/50 truncate">{t.tier || "—"} · {t.created_at?.slice(0, 16)}</div>
            </div>
            <div className="text-white font-bold">${(t.amount || 0).toFixed(2)}</div>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${t.payment_status === "paid" ? "bg-green-500/20 text-green-300" : t.payment_status === "initiated" ? "bg-yellow-500/15 text-yellow-300" : "bg-white/10 text-white/60"}`}>
              {t.payment_status}
            </span>
            <code className="text-[10px] text-white/40 truncate max-w-[180px]" title={t.session_id}>{(t.session_id || "").slice(0, 18)}…</code>
          </div>
        ))}
      </div>
    </div>
  );
}

// =========================================================
// PPV SPLITS — admin-managed pay-per-view revenue splits
// =========================================================
function PpvSplitsTab() {
  const [data, setData] = useState(null);
  const [gPct, setGPct] = useState(70);
  const [creatorId, setCreatorId] = useState("");
  const [creatorPct, setCreatorPct] = useState(70);

  const load = async () => {
    const r = await api.get("/admin/ppv-splits");
    setData(r.data);
    setGPct(r.data.global.creator_pct);
  };
  useEffect(() => { load(); }, []);

  const saveGlobal = async () => {
    await api.put("/admin/ppv-splits/global", { creator_pct: Number(gPct) });
    toast.success("Global split updated");
    load();
  };
  const addOverride = async () => {
    if (!creatorId.trim()) { toast.error("Paste a creator_id"); return; }
    await api.put(`/admin/ppv-splits/creator/${creatorId.trim()}`, { creator_pct: Number(creatorPct) });
    toast.success("Override saved");
    setCreatorId(""); setCreatorPct(70);
    load();
  };
  const removeOverride = async (cid) => {
    await api.delete(`/admin/ppv-splits/creator/${cid}`);
    toast.info("Override removed");
    load();
  };

  if (!data) return <div className="text-white/60">Loading…</div>;

  return (
    <div data-testid="admin-ppv-splits">
      <SectionTitle>Pay-Per-View Revenue Splits</SectionTitle>
      <p className="text-white/60 text-sm mb-4">Set how PPV sales are divided between the creator and the network. Creator-specific overrides win over the global default.</p>

      <div className="tv-glass rounded-2xl p-5">
        <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2">Global default</div>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              data-testid="ppv-global-slider"
              type="range" min="0" max="100"
              value={gPct}
              onChange={(e) => setGPct(e.target.value)}
              className="w-full"
            />
            <div className="flex justify-between text-xs mt-1">
              <span className="text-tv-accent font-bold">Creator {gPct}%</span>
              <span className="text-white/60">Network {100 - gPct}%</span>
            </div>
          </div>
          <button data-testid="ppv-global-save" onClick={saveGlobal} className="bg-tv-accent text-black font-bold px-5 py-2 rounded-full">Save</button>
        </div>
      </div>

      <div className="tv-glass rounded-2xl p-5 mt-4">
        <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2">Add creator-specific override</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <input data-testid="ppv-override-creator-id" placeholder="creator user_id" value={creatorId} onChange={(e) => setCreatorId(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white sm:col-span-2" />
          <div className="flex items-center gap-2">
            <input data-testid="ppv-override-pct" type="number" min="0" max="100" value={creatorPct} onChange={(e) => setCreatorPct(e.target.value)} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            <button data-testid="ppv-override-add" onClick={addOverride} className="bg-tv-accent text-black font-bold px-4 py-2 rounded-full whitespace-nowrap">Add</button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2">Active overrides ({data.overrides.length})</div>
        {data.overrides.length === 0 ? (
          <div className="text-white/60 text-sm">None — all PPV revenue uses the global default above.</div>
        ) : (
          <div className="space-y-2">
            {data.overrides.map((o) => (
              <div key={o.creator_id} data-testid={`ppv-override-${o.creator_id}`} className="tv-glass rounded-xl p-3 flex items-center gap-3">
                <code className="flex-1 text-white/80 text-sm">{o.creator_id}</code>
                <div className="text-sm"><span className="text-tv-accent font-bold">{o.creator_pct}%</span> / {o.network_pct}%</div>
                <button data-testid={`ppv-override-delete-${o.creator_id}`} onClick={() => removeOverride(o.creator_id)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// =========================================================
// DROPSHIP — admin-managed catalog
// =========================================================
function DropshipAdminTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", supplier_price_usd: "", suggested_price_usd: "", vendor: "", category: "general", images: "" });

  const load = async () => {
    const { data } = await api.get("/shop/dropship-catalog");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.supplier_price_usd) { toast.error("Title + supplier price required"); return; }
    await api.post("/admin/dropship-catalog", {
      ...form,
      supplier_price_usd: Number(form.supplier_price_usd),
      suggested_price_usd: Number(form.suggested_price_usd || form.supplier_price_usd * 2),
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Catalog item added");
    setForm({ title: "", supplier_price_usd: "", suggested_price_usd: "", vendor: "", category: "general", images: "" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this catalog item?")) return;
    await api.delete(`/admin/dropship-catalog/${id}`);
    toast.info("Removed");
    load();
  };

  return (
    <div data-testid="admin-dropship-tab">
      <SectionTitle>Dropship Catalog</SectionTitle>
      <p className="text-white/60 text-sm mb-4">Creators can import these to their stores. You set supplier pricing; they set their own markup.</p>
      <form onSubmit={add} data-testid="dropship-create-form" className="tv-glass rounded-2xl p-5 grid md:grid-cols-2 gap-3 mb-6">
        <input data-testid="dropship-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" required />
        <input data-testid="dropship-vendor" placeholder="Vendor / supplier" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="dropship-supplier-price" type="number" step="0.01" placeholder="Supplier cost USD" value={form.supplier_price_usd} onChange={(e) => setForm({ ...form, supplier_price_usd: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" required />
        <input data-testid="dropship-suggested-price" type="number" step="0.01" placeholder="Suggested sell USD" value={form.suggested_price_usd} onChange={(e) => setForm({ ...form, suggested_price_usd: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="dropship-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="dropship-images" placeholder="Image URLs (comma-separated)" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <button data-testid="dropship-create-btn" type="submit" className="md:col-span-2 bg-tv-accent text-black font-bold py-2 rounded-full">Add to catalog</button>
      </form>

      {items.length === 0 ? <div className="text-white/60">No catalog items yet.</div> : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} data-testid={`dropship-row-${it.id}`} className="tv-glass rounded-xl p-3 flex items-center gap-3">
              <img src={(it.images || [])[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=120&q=80"} alt="" className="w-14 h-14 rounded object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold truncate">{it.title}</div>
                <div className="text-xs text-white/50">{it.vendor} · {it.category}</div>
              </div>
              <div className="text-xs text-right">
                <div className="text-white/50">Cost ${it.supplier_price_usd.toFixed(2)}</div>
                <div className="text-tv-accent font-bold">Sell ${it.suggested_price_usd.toFixed(2)}</div>
              </div>
              <button data-testid={`dropship-delete-${it.id}`} onClick={() => remove(it.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// ORDERS — admin view
// =========================================================
function OrdersAdminTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data || [])).catch(() => {}); }, []);

  return (
    <div data-testid="admin-orders-tab">
      <SectionTitle>All orders</SectionTitle>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <AwardsStat label="Total orders" value={orders.length.toLocaleString()} />
        <AwardsStat label="Paid orders" value={orders.filter((o) => o.status === "paid").length.toLocaleString()} />
        <AwardsStat label="GMV" value={`$${orders.filter((o) => o.status === "paid").reduce((s, o) => s + (o.subtotal || 0), 0).toFixed(2)}`} />
      </div>
      {orders.length === 0 ? <div className="text-white/60">No orders yet.</div> : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="tv-glass rounded-xl p-3 flex items-center gap-3 text-sm">
              <code className="text-white/60 text-xs w-24 truncate">{o.id.slice(0, 10)}</code>
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{o.user_email}</div>
                <div className="text-xs text-white/50">{o.items.length} item(s) · {o.created_at?.slice(0, 16)}</div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${o.status === "paid" ? "bg-green-500/20 text-green-300" : "bg-yellow-500/15 text-yellow-300"}`}>{o.status}</span>
              <div className="text-tv-accent font-bold">${o.subtotal.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// =========================================================
// COUPONS — admin CRUD
// =========================================================
function CouponsAdminTab() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_type: "pct", value: 10, min_subtotal: 0, max_redemptions: 0 });

  const load = async () => { const r = await api.get("/admin/coupons"); setCoupons(r.data || []); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...form, value: Number(form.value), min_subtotal: Number(form.min_subtotal), max_redemptions: Number(form.max_redemptions) });
      toast.success("Coupon created");
      setForm({ code: "", discount_type: "pct", value: 10, min_subtotal: 0, max_redemptions: 0 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/coupons/${id}`); load(); };

  return (
    <div data-testid="admin-coupons-tab">
      <SectionTitle>Coupon codes</SectionTitle>
      <form onSubmit={create} data-testid="coupon-create-form" className="tv-glass rounded-2xl p-5 grid md:grid-cols-5 gap-3 mb-6">
        <input data-testid="coupon-code-input" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white uppercase" required />
        <select data-testid="coupon-type-select" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="bg-[#0B1120] border border-white/10 rounded-lg px-3 py-2 text-white">
          <option value="pct">% off</option>
          <option value="flat">$ off</option>
        </select>
        <input data-testid="coupon-value-input" type="number" step="0.01" placeholder={form.discount_type === "pct" ? "%" : "$"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="coupon-min-input" type="number" step="0.01" placeholder="Min subtotal" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <input data-testid="coupon-max-input" type="number" placeholder="Max redemptions (0=∞)" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
        <button data-testid="coupon-create-btn" type="submit" className="md:col-span-5 bg-tv-accent text-black font-bold py-2 rounded-full">Create Coupon</button>
      </form>

      {coupons.length === 0 ? <div className="text-white/60">No coupons yet.</div> : (
        <div className="space-y-2">
          {coupons.map((c) => (
            <div key={c.id} data-testid={`coupon-row-${c.id}`} className="tv-glass rounded-xl p-3 flex items-center gap-3">
              <code className="text-tv-accent font-bold text-lg w-32">{c.code}</code>
              <div className="flex-1 min-w-0 text-sm text-white/70">
                {c.discount_type === "pct" ? `${c.value}% off` : `$${c.value} off`}
                {c.min_subtotal > 0 && ` · min $${c.min_subtotal}`}
                {c.max_redemptions && ` · ${c.redemptions_count}/${c.max_redemptions} used`}
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${c.is_active ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/50"}`}>{c.is_active ? "Active" : "Off"}</span>
              <button data-testid={`coupon-delete-${c.id}`} onClick={() => remove(c.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// FULFILLMENT QUEUE — dropship orders awaiting supplier forwarding
// =========================================================
function FulfillmentAdminTab() {
  const [queue, setQueue] = useState([]);

  const load = async () => { const r = await api.get("/admin/fulfillment/queue"); setQueue(r.data || []); };
  useEffect(() => { load(); }, []);

  const forward = async (oid) => {
    const { data } = await api.post(`/admin/fulfillment/${oid}/forward`);
    toast.success(`Forwarded · tracking ${data.tracking_ref}`);
    load();
  };

  return (
    <div data-testid="admin-fulfillment-tab">
      <SectionTitle>Dropship fulfillment queue</SectionTitle>
      <p className="text-white/60 text-sm mb-4">Paid orders containing dropship products. Click "Forward" to push the order to the supplier.</p>
      {queue.length === 0 ? <div data-testid="fulfillment-empty" className="text-white/60">No pending dropship orders.</div> : (
        <div className="space-y-3">
          {queue.map((o) => (
            <div key={o.id} data-testid={`fulfillment-row-${o.id}`} className="tv-glass rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <code className="text-white/60 text-xs">#{o.id.slice(0, 10)}</code>
                  <span className="ml-2 text-white/70 text-sm">{o.user_email}</span>
                </div>
                <button data-testid={`fulfillment-forward-${o.id}`} onClick={() => forward(o.id)} className="bg-tv-accent text-black font-bold px-4 py-1.5 rounded-full text-sm">Forward to supplier</button>
              </div>
              <div className="text-xs text-white/60 mb-2">Ship to: {o.shipping?.name}, {o.shipping?.line1}, {o.shipping?.city} {o.shipping?.postal_code}, {o.shipping?.country}</div>
              <div className="space-y-1">
                {o.dropship_items.map((it) => (
                  <div key={it.product_id} className="flex items-center gap-2 text-sm">
                    <span className="text-white/90">{it.title} × {it.qty}</span>
                    <span className="text-[10px] uppercase bg-purple-500/20 text-purple-300 font-bold px-2 py-0.5 rounded">{it.vendor}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubscriptionsAdminTab() {
  const [plans, setPlans] = useState([]);
  const [dirty, setDirty] = useState({});

  const load = async () => {
    const { data } = await api.get("/subscription-plans");
    setPlans(data);
    setDirty({});
  };
  useEffect(() => { load(); }, []);

  const save = async (tier) => {
    const patch = dirty[tier];
    if (!patch) return;
    try {
      await api.put(`/admin/subscription-plans/${tier}`, patch);
      toast.success(`${tier} updated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div data-testid="admin-subscriptions-tab" className="max-w-4xl space-y-4">
      <div>
        <h2 className="font-display font-black text-3xl text-white">Subscription Plans</h2>
        <p className="text-white/60 text-sm mt-1">Adjust monthly/yearly pricing and benefits. Changes apply to new signups immediately.</p>
      </div>
      {plans.map((p) => {
        const d = dirty[p.tier] || {};
        const val = (k, fallback) => d[k] !== undefined ? d[k] : (p[k] ?? fallback);
        const patch = (k, v) => setDirty((prev) => ({ ...prev, [p.tier]: { ...(prev[p.tier] || {}), [k]: v } }));
        return (
          <div key={p.tier} data-testid={`plan-row-${p.tier}`} className="tv-glass rounded-xl border border-white/10 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-tv-accent font-bold">{p.tier.replace("_", " ")}</div>
                <input data-testid={`plan-label-${p.tier}`} value={val("label", "")} onChange={(e) => patch("label", e.target.value)} className="font-display font-bold text-xl text-white bg-transparent border-b border-white/10 focus:border-tv-accent outline-none w-full" />
              </div>
              <label className="text-xs text-white/70 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={val("is_active", true)} onChange={(e) => patch("is_active", e.target.checked)} data-testid={`plan-active-${p.tier}`} className="accent-tv-accent" />
                Active
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Monthly USD</span>
                <input type="number" step="0.01" data-testid={`plan-monthly-${p.tier}`} value={val("monthly_usd", 0)} onChange={(e) => patch("monthly_usd", parseFloat(e.target.value))} className="w-full mt-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Yearly USD</span>
                <input type="number" step="0.01" data-testid={`plan-yearly-${p.tier}`} value={val("yearly_usd", 0)} onChange={(e) => patch("yearly_usd", parseFloat(e.target.value))} className="w-full mt-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
              </label>
            </div>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Benefits (one per line)</span>
              <textarea data-testid={`plan-benefits-${p.tier}`} value={Array.isArray(val("benefits", p.benefits)) ? val("benefits", p.benefits).join("\n") : ""} onChange={(e) => patch("benefits", e.target.value.split("\n").filter(Boolean))} rows={3} className="w-full mt-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-sm resize-none" />
            </label>
            <div className="flex justify-end mt-3">
              <button data-testid={`plan-save-${p.tier}`} onClick={() => save(p.tier)} disabled={!dirty[p.tier]} className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-5 py-2 rounded-full text-sm">Save</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PromosAdminTab() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", description: "", discount_type: "pct", value: 10, max_redemptions: 0 });

  const load = async () => { const { data } = await api.get("/admin/subscription-promos"); setPromos(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) return toast.error("code required");
    try { await api.post("/admin/subscription-promos", form); toast.success("Promo created"); setForm({ code: "", description: "", discount_type: "pct", value: 10, max_redemptions: 0 }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this promo?")) return;
    await api.delete(`/admin/subscription-promos/${id}`); load();
  };

  return (
    <div data-testid="admin-promos-tab" className="max-w-4xl">
      <h2 className="font-display font-black text-3xl text-white">Subscription Promos</h2>
      <p className="text-white/60 text-sm mt-1 mb-4">Create promo codes for subscription discounts. Used at checkout via <code className="text-tv-accent">/subscription-promos/validate/:code</code>.</p>

      <div className="tv-glass rounded-xl p-4 border border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input data-testid="promo-code-input" placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white font-mono uppercase" />
          <select data-testid="promo-type-input" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white">
            <option value="pct">% off</option>
            <option value="fixed">$ off</option>
          </select>
          <input data-testid="promo-value-input" type="number" step="0.01" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <input data-testid="promo-max-input" type="number" placeholder="Max redemptions (0 = ∞)" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: parseInt(e.target.value) || 0 })} className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white" />
          <button data-testid="promo-create-btn" onClick={create} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 rounded">Create</button>
        </div>
        <input data-testid="promo-desc-input" placeholder="Description (shown at checkout)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full mt-2 bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-sm" />
      </div>

      <div className="mt-5 space-y-2" data-testid="promo-list">
        {promos.map((p) => (
          <div key={p.id} data-testid={`promo-row-${p.code}`} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
            <div className="font-mono font-bold text-tv-accent">{p.code}</div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold">{p.discount_type === "pct" ? `${p.value}% off` : `$${p.value} off`}</div>
              <div className="text-[11px] text-white/50 truncate">{p.description || "—"} · redeemed {p.redemptions_count}{p.max_redemptions ? `/${p.max_redemptions}` : ""}</div>
            </div>
            <button onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {promos.length === 0 && <div className="text-white/40 text-sm">No promos yet.</div>}
      </div>
    </div>
  );
}


